import { tturl } from "~/util/Lang";
import axios from "axios";
import { and, eq, or } from "drizzle-orm";
import { z } from "zod";
import { pg } from "~/pg.server";
import { Realm } from "./Lang";

// Discrod
const HOST = DEV ? "http://localhost:3000" : "https://l2hub.info";
export const DISCORD_API_ENDPOINT = "https://discord.com/api/v10";
export const DISCORD_CLIENT_ID = "1347666967653519402";
export const DISCORD_CLIENT_SECRET = "7sEMZlFncRpxeYL8GI9OzH5zT55Gax9H";
export const DISCORD_REDIRECT_URI = "/auth/discord";

export function getDiscordAuthUrl(realm: Realm, state?: { from: string }) {
  const redirectUri = encodeURIComponent(
    HOST + tturl({ game: "gf", lang: realm.lang }, DISCORD_REDIRECT_URI)
  );
  const stateUri = encodeURIComponent(JSON.stringify(state));
  return `https://discord.com/oauth2/authorize?client_id=1347666967653519402&response_type=code&redirect_uri=${redirectUri}&scope=identify+email${
    state ? `&state=${stateUri}` : ``
  }`;
}

const DiscordToken = z.object({
  token_type: z.string(),
  access_token: z.string(),
  expires_in: z.number(),
  refresh_token: z.string(),
  scope: z.string(),
});
export type DiscordToken = z.infer<typeof DiscordToken>;

const DiscordUser = z.object({
  id: z.string(),
  username: z.string(),
  avatar: z.string().nullable(),
  email: z.string().nullable(),
  verified: z.boolean().optional(),
});
export type DiscordUser = z.infer<typeof DiscordUser>;

export async function getDiscordToken(props: {
  code: string;
  realm: Realm;
}): Promise<DiscordToken> {
  const data = new URLSearchParams({
    client_id: DISCORD_CLIENT_ID,
    client_secret: DISCORD_CLIENT_SECRET,
    grant_type: "authorization_code",
    code: props.code,
    redirect_uri:
      HOST +
      tturl({ game: "gf", lang: props.realm.lang }, DISCORD_REDIRECT_URI),
  });

  try {
    const res = await axios.post(
      `${DISCORD_API_ENDPOINT}/oauth2/token`,
      data.toString(),
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      }
    );
    return DiscordToken.parse(res.data);
  } catch (err: any) {
    console.error("Discord token error:", err.response?.data || err);
    throw new Error("DISCORD_TOKEN_ERROR");
  }
}

export async function getDiscordUser(
  accessToken: string
): Promise<DiscordUser> {
  try {
    const res = await axios.get(`${DISCORD_API_ENDPOINT}/users/@me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return DiscordUser.parse(res.data);
  } catch (err: any) {
    console.error("Discord user fetch error:", err.response?.data || err);
    throw new Error("Failed to get Discord user");
  }
}

export async function findOrCreateUser(discordData: DiscordUser) {
  try {
    const [discordLinked] = await pg.db
      .select({ userId: pg.userDiscords.userId })
      .from(pg.userDiscords)
      .where(eq(pg.userDiscords.discordId, discordData.id))
      .limit(1);

    if (discordLinked)
      return { t: "discord_exists" as const, userId: discordLinked.userId };

    const [matchStrict] = await pg.db
      .select({ id: pg.users.id, login: pg.users.login })
      .from(pg.users)
      .where(eq(pg.users.login, discordData.username))
      .limit(1);

    if (matchStrict) return { t: "match" as const, user: matchStrict };

    return { t: "not_found" as const };
  } catch (err) {
    console.error("findOrCreateUser error:", err);
    return { t: "error" as const };
  }
}
